/**
 * The id of a turn that was still in flight when the screen went away.
 *
 * ── WHY THIS FILE EXISTS ────────────────────────────────────────────────────
 * A finished stream is never OFFERED on reconnect: the server answers
 * `resume_none` with reason `idle`, because a completed stream is not an active
 * one. The buffered answer is reachable for ten minutes, and only by ACKing the
 * ORIGINAL request id — an id the client is never re-offered and must therefore
 * have remembered (`T-58`, and the docblock on `inFlightRequestId`).
 *
 * It used to be remembered in a field, which meant it survived exactly as long
 * as the `TrainerSocket` instance. The instance dies on unmount, and the chat
 * screen unmounts on every dock tap — `router.replace` swaps the route out — so
 * the one handle to the answer was thrown away by the most ordinary action in
 * the app. Killing the app did the same thing for the same reason.
 *
 * So it lives here, outside the process. Async Storage rather than
 * Expo SecureStore for the same reason the timer's mute uses it: a request
 * id is not a credential — it names a buffer inside a Durable Object that only
 * this account's ticket can open, and the keystore's cost belongs to secrets.
 *
 * ── AND THE OUTBOUND HALF, SINCE `D192` ─────────────────────────────────────
 * The question, or the widget answer, rides with the id. A turn queued while
 * offline, or one whose request never left before the app was killed, used to
 * be a bubble nobody ever sent: the composer had been cleared and the text
 * lived nowhere. Now the next mount reads the transcript, sees the question is
 * not in it, and sends it — the outbox the workout has had since `D163`, for
 * the one write the chat makes.
 *
 * ── WHAT IS DELIBERATELY NOT STORED ─────────────────────────────────────────
 * The partial text. A partial is only ever shown under `D77`'s rules — either
 * the end marker arrived or the answer is discarded — and a partial restored
 * from storage has no marker to judge, so it could only ever be shown as a
 * severed answer, which is the thing `D77` overturned. The transcript comes
 * back from the server, whole, or not at all.
 */
/**
 * ── ONE KEY PER CONVERSATION (plan D32, T44) ────────────────────────────────
 * The key used to be one fixed string with the conversation id inside the
 * value. That held while every client spoke only `default`; with a second
 * conversation a turn started in B overwrote A's, and an app that died
 * mid-turn in A lost A's resume-by-ACK (`T-58`) the moment B was opened.
 *
 * The id inside the value stays, and is still checked: it costs nothing, and
 * it is what lets the migration below put a legacy entry under the key it
 * belongs to.
 */
const KEY_PREFIX = "mypocketgym.chat.pending_turn.";
/**
 * The key every build before T44 wrote. Read only by
 * {@link migrateLegacyPendingTurn}; nothing writes it any more.
 */
export const LEGACY_PENDING_TURN_KEY = "mypocketgym.chat.pending_turn";
export function pendingTurnKey(conversationId) {
    return `${KEY_PREFIX}${conversationId}`;
}
/**
 * ── A DAY, WHERE IT WAS TEN MINUTES (`D192`) ────────────────────────────────
 * Ten minutes was the server's retention of a completed stream's chunks: the
 * record existed to be ACKed, and an id older than that named nothing. The
 * record no longer exists to be ACKed — the next mount reads the transcript
 * and asks where the turn stands — so its life is the outbox's: long enough
 * for a question typed on a plane to be sent on landing, short enough that a
 * question from last week is not sent into today's conversation.
 */
const RETENTION_MS = 24 * 60 * 60 * 1000;
/**
 * Move the pre-T44 entry under its conversation's own key, once.
 *
 * ── IT RUNS BEFORE EVERY READ AND EVERY WRITE, NOT ONCE AT BOOT ─────────────
 * A pending turn left by the previous build is an answer the server holds for
 * ten minutes and nobody else knows how to reach. If the first write under the
 * new key happened before this, the legacy entry would be orphaned — so every
 * entry point below calls it first. It costs one `getItem` returning `null`
 * after the first run, and it is idempotent: the legacy key is removed on the
 * way out, so a second run finds nothing to do.
 *
 * The legacy value names its own conversation (in practice always `default`,
 * the only id any build before T44 spoke), and moves to THAT id's key. An
 * entry already under the new key wins: it was written by this build, after
 * the one being migrated. A value that does not parse is dropped — the old
 * reader would have refused it too.
 */
export async function migrateLegacyPendingTurn(store) {
    try {
        const raw = await store.getItem(LEGACY_PENDING_TURN_KEY);
        if (raw === null) {
            return;
        }
        const parsed = parsePendingTurn(raw);
        if (parsed !== null) {
            const key = pendingTurnKey(parsed.conversationId);
            if ((await store.getItem(key)) === null) {
                await store.setItem(key, raw);
            }
        }
        await store.removeItem(LEGACY_PENDING_TURN_KEY);
    }
    catch {
        // Storage refused. The legacy entry stays where it is, and the next call
        // tries again; nothing here may cost the turn.
    }
}
export async function rememberPendingTurn(conversationId, requestId, now, store, outbound = {}) {
    await migrateLegacyPendingTurn(store);
    const pending = {
        conversationId,
        requestId,
        startedAt: now,
        ...(outbound.question === undefined ? {} : { question: outbound.question }),
        ...(outbound.toolResult === undefined
            ? {}
            : { toolResult: outbound.toolResult }),
    };
    try {
        await store.setItem(pendingTurnKey(conversationId), JSON.stringify(pending));
    }
    catch {
        // Storage refused. The turn still works; only its recovery is lost, and a
        // throw here would take the question with it.
    }
}
/** Forget THIS conversation's turn; another conversation's is untouched. */
export async function forgetPendingTurn(conversationId, store) {
    await migrateLegacyPendingTurn(store);
    try {
        await store.removeItem(pendingTurnKey(conversationId));
    }
    catch {
        // As above. A stale id is harmless: it is checked against the retention
        // window and against the conversation before it is ever ACKed.
    }
}
/**
 * The turn left in flight, or `null` — for a different conversation, for one
 * past the retention window, or for anything that did not parse.
 */
export async function readPendingTurn(conversationId, now, store) {
    // Inside the read, because the resume path reads the pending turn when the
    // hook starts (`use-trainer-chat.ts`): the first read after an upgrade is
    // the one that must find the previous build's turn.
    await migrateLegacyPendingTurn(store);
    let raw = null;
    try {
        raw = await store.getItem(pendingTurnKey(conversationId));
    }
    catch {
        return null;
    }
    if (raw === null) {
        return null;
    }
    const parsed = parsePendingTurn(raw);
    if (parsed === null) {
        return null;
    }
    if (parsed.conversationId !== conversationId) {
        return null;
    }
    if (now - parsed.startedAt > RETENTION_MS) {
        return null;
    }
    return {
        requestId: parsed.requestId,
        ...(isQuestion(parsed.question) ? { question: parsed.question } : {}),
        ...(isRecord(parsed.toolResult) ? { toolResult: parsed.toolResult } : {}),
    };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isQuestion(value) {
    if (!isRecord(value)) {
        return false;
    }
    return typeof value.id === "string" && typeof value.text === "string";
}
function parsePendingTurn(raw) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    return isPendingTurn(parsed) ? parsed : null;
}
function isPendingTurn(value) {
    if (typeof value !== "object" || value === null) {
        return false;
    }
    const candidate = value;
    return (typeof candidate.conversationId === "string" &&
        typeof candidate.requestId === "string" &&
        typeof candidate.startedAt === "number");
}
//# sourceMappingURL=pending-turn.js.map